import { useEffect, useState } from 'react';
import { auth, getCurrentUser, authInitialized } from '../firebase';
import { onAuthStateChanged } from 'firebase/auth';
import { f7 } from 'framework7-react';
import { Capacitor } from '@capacitor/core';
import { FirebaseAuthentication } from '@capacitor-firebase/authentication';
import { checkExpiry } from '../utils/sessionManager';
import type { PluginListenerHandle } from '@capacitor/core';

/**
 * useAuth hook
 * - Monitors the authentication state of the user.
 * - Optionally redirects to login if not authenticated.
 *
 * @param redirectOnFail - If true (default), redirects to "/" when unauthenticated.
 */
export function useAuth(redirectOnFail: boolean = true) {
  const [loadingAuth, setLoadingAuth] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  useEffect(() => {
    let unsubscribe: (() => void) | undefined;
    let listener: Promise<PluginListenerHandle> | undefined;
    let isMounted = true;

    const handleAuthChange = (user: unknown) => {
      if (!isMounted) return;
      const authenticated = !!user;
      setIsAuthenticated(authenticated);
      setLoadingAuth(false);

      if (!authenticated && redirectOnFail) {
        setTimeout(() => {
          if (isMounted) {
            f7.views.main?.router.navigate('/');
          }
        }, 500);
      }
    };

    // Check session expiry and set up auth listeners
    const initializeAuth = async () => {
      try {
        // Check if 120-day session has expired
        const sessionValid = await checkExpiry();

        if (!sessionValid) {
          // Session expired - checkExpiry() already logged out
          return;
        }

        // Session is valid - set up auth listeners
        if (Capacitor.isNativePlatform()) {
          try {
            // Initialize with current user on native platforms
            const user = await getCurrentUser();
            handleAuthChange(user);
            listener = FirebaseAuthentication.addListener('authStateChange', (event) =>
              handleAuthChange(event.user)
            );
          } catch (error) {
            console.error('Failed to initialize native auth:', error);
            handleAuthChange(null); // Treat as logged out
          }
        } else {
          unsubscribe = onAuthStateChanged(auth, (user) => handleAuthChange(user));
        }
      } catch (error) {
        console.error('Session check or initialization failed:', error);
        if (isMounted) {
          setLoadingAuth(false);
        }
      }
    };

    // Start initialization after auth is ready
    authInitialized
      .then(() => initializeAuth())
      .catch((error) => {
        console.error('Auth initialization failed:', error);
        if (isMounted) {
          setLoadingAuth(false);
        }
      });

    return () => {
      isMounted = false;
      unsubscribe?.();
      listener?.then((h) => h?.remove?.());
    };
  }, [redirectOnFail]);

  return { loadingAuth, isAuthenticated };
}

// This hook checks the authentication state of the user and redirects them if they are not authenticated.